import { z } from 'zod';
import { paginationQuerySchema, queryBooleanSchema } from './pagination.js';
/**
 * Zone -> Area -> Branch (D81). A zone has many areas, an area has many
 * branches. Every branch belongs to exactly one area; branches with no known
 * area sit in their zone's "غير محدد" area, never nowhere and never dropped.
 */
/** The catch-all area of a zone: branches whose area is not known yet. */
export const UNASSIGNED_AREA = { ar: 'غير محدد', en: 'Unassigned' };
/** Code suffix of a zone's catch-all area: `<ZONE CODE>-UNASSIGNED`. */
export const UNASSIGNED_AREA_CODE_SUFFIX = 'UNASSIGNED';
/** The zone the migration and the importer create when nothing names one. */
export const DEFAULT_ZONE = { code: 'GENERAL', ar: 'المنطقة العامة', en: 'General zone' };
const ARABIC_MARKS = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/g;
const LATIN_MARKS = /[̀-ͯ]/g;
const LETTER_FOLDS = { أ: 'ا', إ: 'ا', آ: 'ا', ٱ: 'ا', ؤ: 'و', ئ: 'ي', ة: 'ه', ى: 'ي' };
function foldDigit(ch) {
    const code = ch.charCodeAt(0);
    if (code >= 0x0660 && code <= 0x0669)
        return String(code - 0x0660);
    if (code >= 0x06f0 && code <= 0x06f9)
        return String(code - 0x06f0);
    return ch;
}
/**
 * The comparable form of a zone or area name (D82): trimmed, case-folded,
 * Arabic diacritics and tatweel removed, alef/hamza forms, taa marbuta and
 * alef maqsura unified, Arabic-Indic digits folded, punctuation and repeated
 * spaces collapsed. "مدينة نصر", "مدينه  نصر" and "مَدينة نصر" are one area.
 */
export function normalizeGeoName(text) {
    const folded = text.normalize('NFKD').replace(LATIN_MARKS, '').replace(ARABIC_MARKS, '').toLocaleLowerCase('en');
    let out = '';
    for (const ch of folded)
        out += LETTER_FOLDS[ch] ?? foldDigit(ch);
    return out
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}
/** A code derived from a name when none is given: Latin letters and digits kept, the rest a short hash. */
export function deriveGeoCode(name, prefix = '') {
    const latin = normalizeGeoName(name)
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 20);
    if (latin.length >= 2)
        return `${prefix}${latin}`.slice(0, 30);
    // Arabic names: a stable short hash of the normalised name (FNV-1a), so re-imports find the same code.
    let hash = 0x811c9dc5;
    for (const ch of normalizeGeoName(name)) {
        hash ^= ch.codePointAt(0);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return `${prefix}A${hash.toString(36).toUpperCase()}`.slice(0, 30);
}
const geoName = z
    .string()
    .transform((v) => v.normalize('NFC').trim().replace(/\s+/g, ' '))
    .pipe(z.string().min(1, 'Name is required').max(80));
const geoNameEn = geoName.nullable();
const geoCode = z
    .string()
    .trim()
    .min(1)
    .max(30)
    .regex(/^[A-Za-z0-9._-]+$/, 'Code may contain letters, digits, dot, dash and underscore')
    .transform((v) => v.toUpperCase());
const note = z.string().trim().max(500).nullable();
export const zoneDtoSchema = z.object({
    id: z.string(),
    code: z.string(),
    nameAr: z.string(),
    nameEn: z.string().nullable(),
    isActive: z.boolean(),
    managerNote: z.string().nullable(),
    areaCount: z.number().int(),
    branchCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const areaDtoSchema = z.object({
    id: z.string(),
    zoneId: z.string(),
    zoneNameAr: z.string(),
    zoneNameEn: z.string().nullable(),
    code: z.string(),
    nameAr: z.string(),
    nameEn: z.string().nullable(),
    isActive: z.boolean(),
    /** The zone's catch-all "غير محدد" area. Cannot be renamed, moved or deleted. */
    isUnassigned: z.boolean(),
    managerNote: z.string().nullable(),
    branchCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createZoneRequestSchema = z.object({
    code: geoCode,
    nameAr: geoName,
    nameEn: geoNameEn.default(null),
    managerNote: note.default(null),
});
export const updateZoneRequestSchema = z
    .object({ code: geoCode, nameAr: geoName, nameEn: geoNameEn, managerNote: note, isActive: z.boolean() })
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');
export const createAreaRequestSchema = z.object({
    zoneId: z.uuid(),
    code: geoCode,
    nameAr: geoName,
    nameEn: geoNameEn.default(null),
    managerNote: note.default(null),
});
export const updateAreaRequestSchema = z
    .object({ zoneId: z.uuid(), code: geoCode, nameAr: geoName, nameEn: geoNameEn, managerNote: note, isActive: z.boolean() })
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');
export const listZonesQuerySchema = z.object({ includeInactive: queryBooleanSchema.default(false) });
export const listAreasQuerySchema = paginationQuerySchema.extend({
    zoneId: z.uuid().optional(),
    search: z.string().trim().max(100).optional(),
    includeInactive: queryBooleanSchema.default(false),
    pageSize: z.coerce.number().int().min(1).max(500).default(500),
});
/** Most branches one re-assignment request may move. */
export const MAX_BRANCHES_PER_REASSIGN = 1000;
/** Moves branches into an area (bulk re-assignment from the branches table). */
export const assignBranchesToAreaRequestSchema = z.object({
    areaId: z.uuid(),
    branchIds: z.array(z.uuid()).min(1).max(MAX_BRANCHES_PER_REASSIGN),
});
export const assignBranchesToAreaResponseSchema = z.object({
    moved: z.number().int(),
    unchanged: z.number().int(),
    notFound: z.number().int(),
});
/** Filters by zone and area, shared by every list that shows branches or visits at branches. */
export const geoFilterShape = {
    zoneId: z.uuid().optional(),
    areaId: z.uuid().optional(),
};
/** The whole hierarchy, small enough to load once for every filter dropdown. */
export const geographyTreeSchema = z.object({
    zones: z.array(z.object({
        id: z.string(),
        code: z.string(),
        nameAr: z.string(),
        nameEn: z.string().nullable(),
        isActive: z.boolean(),
        areas: z.array(z.object({
            id: z.string(),
            code: z.string(),
            nameAr: z.string(),
            nameEn: z.string().nullable(),
            isActive: z.boolean(),
            isUnassigned: z.boolean(),
            branchCount: z.number().int(),
        })),
    })),
});
/** Arabic first, English when the viewer reads English and there is one. */
export function geoLabel(item, language) {
    return language === 'en' && item.nameEn ? item.nameEn : item.nameAr;
}
/**
 * Finds the existing zone/area a typed name means, or says it is new. Used by
 * the importer: trimmed, case-insensitive and Arabic-aware (normalizeGeoName),
 * matched on the Arabic name, the English name or the code.
 */
export function matchGeoName(candidates, typed) {
    const key = normalizeGeoName(typed);
    if (!key)
        return null;
    return (candidates.find((c) => normalizeGeoName(c.nameAr) === key || (c.nameEn !== null && normalizeGeoName(c.nameEn) === key) || normalizeGeoName(c.code) === key) ?? null);
}
/** What the migration did with the old free-text areas (reported once, in the audit log and BATCH_REPORT.md). */
export const geographyMigrationReportSchema = z.object({
    zonesCreated: z.number().int(),
    areasCreated: z.number().int(),
    branchesTotal: z.number().int(),
    branchesMatched: z.number().int(),
    branchesUnassigned: z.number().int(),
    distinctFreeTextAreas: z.number().int(),
});
//# sourceMappingURL=geography.js.map